export const TOKEN_KEYS = {
  access: "ielts_access_token",
  refresh: "ielts_refresh_token",
} as const;

const SHARED_MAX_AGE = 60 * 60 * 24 * 7;

function cookieDomain(): string | undefined {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return undefined;
  if (host === "cocouni.ir" || host.endsWith(".cocouni.ir")) return ".cocouni.ir";
  return undefined;
}

function writeCookie(name: string, value: string, maxAge: number): void {
  let cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
  if (window.location.protocol === "https:") cookie += "; Secure";
  const domain = cookieDomain();
  if (domain) cookie += `; Domain=${domain}`;
  document.cookie = cookie;
}

function expireCookie(name: string): void {
  let cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
  if (window.location.protocol === "https:") cookie += "; Secure";
  const domain = cookieDomain();
  if (domain) cookie += `; Domain=${domain}`;
  document.cookie = cookie;
}

export function readCookie(name: string): string {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const item = part.trim();
    if (item.startsWith(prefix)) {
      return decodeURIComponent(item.slice(prefix.length));
    }
  }
  return "";
}

export function persistSharedTokens(tokens: {
  access_token?: string;
  refresh_token?: string;
}): void {
  if (tokens.access_token) {
    writeCookie(TOKEN_KEYS.access, tokens.access_token, SHARED_MAX_AGE);
  }
  if (tokens.refresh_token) {
    writeCookie(TOKEN_KEYS.refresh, tokens.refresh_token, SHARED_MAX_AGE);
  }
}

export function clearSharedTokens(): void {
  expireCookie(TOKEN_KEYS.access);
  expireCookie(TOKEN_KEYS.refresh);
}

/** Copy localStorage ↔ subdomain cookie so app and speaking share one login. */
export function hydrateSharedTokens(): void {
  try {
    const access =
      localStorage.getItem(TOKEN_KEYS.access) || readCookie(TOKEN_KEYS.access);
    const refresh =
      localStorage.getItem(TOKEN_KEYS.refresh) || readCookie(TOKEN_KEYS.refresh);
    if (access) {
      localStorage.setItem(TOKEN_KEYS.access, access);
      writeCookie(TOKEN_KEYS.access, access, SHARED_MAX_AGE);
    }
    if (refresh) {
      localStorage.setItem(TOKEN_KEYS.refresh, refresh);
      writeCookie(TOKEN_KEYS.refresh, refresh, SHARED_MAX_AGE);
    }
  } catch {
    /* ignore */
  }
}

export function isAllowedReturnUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin === window.location.origin) {
      return parsed.pathname.startsWith("/");
    }
    const host = parsed.hostname;
    return (
      host === "speaking.cocouni.ir" ||
      host === "localhost" ||
      host === "127.0.0.1"
    );
  } catch {
    return false;
  }
}

export function followAuthRedirect(
  redirect: string,
  fallback: string,
  routerPush: (path: string) => void
): void {
  if (/^https?:\/\//i.test(redirect) && isAllowedReturnUrl(redirect)) {
    window.location.assign(redirect);
    return;
  }
  if (redirect.startsWith("/") && !redirect.startsWith("//")) {
    routerPush(redirect);
    return;
  }
  routerPush(fallback);
}
